/**
 * 新效果 #276-#278（2026-03-04）
 * 
 * #276 流体动力学模拟 (Fluid Dynamics Simulation)
 * #277 WebGL 着色器粒子 (WebGL Shader Particles)
 * #278 AI 生成动画模式 (AI Generated Animation Pattern)
 */

import React, { useMemo } from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate, spring } from 'remotion';

// ===== #276 流体动力学模拟 =====

interface FluidCell {
  x: number;
  y: number;
  vx: number;
  vy: number;
  density: number;
}

export const FluidDynamicsSimulation: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 生成流体粒子（20x20 网格）
  const gridSize = 20;
  const fluidCells = useMemo(() => {
    const cells: FluidCell[] = [];
    for (let i = 0; i < gridSize; i++) {
      for (let j = 0; j < gridSize; j++) {
        cells.push({
          x: i / gridSize,
          y: j / gridSize,
          vx: 0,
          vy: 0,
          density: 1,
        });
      }
    }
    return cells;
  }, []);

  // 更新流体状态（模拟流体动力学）
  const time = frame / fps;
  const updatedCells = fluidCells.map((cell, index) => {
    // 流体动力学方程（简化版 Navier-Stokes）
    const angle = Math.sin(cell.x * 4 + time * 2) * Math.PI * 2;
    const speed = Math.cos(cell.y * 4 + time * 1.5) * 0.5;
    
    const vx = Math.cos(angle) * speed;
    const vy = Math.sin(angle) * speed;
    
    // 添加湍流效果
    const turbulence = Math.sin(cell.x * 8 + cell.y * 8 + time * 3) * 0.2;
    
    // 计算密度
    const density = 0.5 + Math.sin(cell.x * 2 + cell.y * 2 + time) * 0.5;
    
    return {
      ...cell,
      vx: vx + turbulence,
      vy: vy + turbulence,
      density,
    };
  });

  // 中心文字动画
  const titleOpacity = spring({ frame, fps, config: { damping: 200 } });
  const titleScale = spring({ frame, fps, config: { damping: 15, stiffness: 100 } });

  return (
    <AbsoluteFill style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)' }}>
      {/* 流体网格 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {updatedCells.map((cell, index) => {
          const cx = cell.x * width;
          const cy = cell.y * height;
          const dx = cell.vx * 50;
          const dy = cell.vy * 50;
          
          // HSL 颜色（基于密度）
          const hue = (cell.density * 60 + 180) % 360; // 青色到洋红色
          const saturation = 70;
          const lightness = 50 + cell.density * 20;
          
          return (
            <g key={index}>
              {/* 流体粒子 */}
              <circle
                cx={cx}
                cy={cy}
                r={3 + cell.density * 3}
                fill={`hsl(${hue}, ${saturation}%, ${lightness}%)`}
                opacity={0.6 + cell.density * 0.4}
              />
              
              {/* 速度向量（流线） */}
              <line
                x1={cx}
                y1={cy}
                x2={cx + dx}
                y2={cy + dy}
                stroke={`hsl(${hue}, ${saturation}%, ${lightness + 20}%)`}
                strokeWidth={1 + cell.density}
                opacity={0.4}
              />
            </g>
          );
        })}
        
        {/* 发光滤镜 */}
        <defs>
          <filter id="fluid-glow">
            <feGaussianBlur stdDeviation="2" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${titleScale})`,
          opacity: titleOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            color: '#00ffff',
            textShadow: '0 0 20px rgba(0, 255, 255, 0.5)',
          }}
        >
          流体动力学
        </div>
        <div
          style={{
            fontSize: 30,
            color: '#ffffff',
            marginTop: 10,
            opacity: 0.8,
          }}
        >
          Fluid Dynamics Simulation
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ===== #277 WebGL 着色器粒子 =====

interface ShaderParticle {
  x: number;
  y: number;
  z: number;
  vx: number;
  vy: number;
  vz: number;
  color: string;
}

export const WebGLShaderParticles: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // 生成着色器粒子（200 个）
  const particleCount = 200;
  const particles = useMemo(() => {
    const result: ShaderParticle[] = [];
    for (let i = 0; i < particleCount; i++) {
      // 球面分布
      const theta = (i / particleCount) * Math.PI * 2;
      const phi = Math.acos(2 * Math.random() - 1);
      const r = 200;
      
      result.push({
        x: r * Math.sin(phi) * Math.cos(theta),
        y: r * Math.sin(phi) * Math.sin(theta),
        z: r * Math.cos(phi),
        vx: 0,
        vy: 0,
        vz: 0,
        color: `hsl(${(i / particleCount) * 360}, 80%, 60%)`,
      });
    }
    return result;
  }, []);

  // 更新粒子位置（着色器风格动画）
  const time = frame / fps;
  const updatedParticles = particles.map((particle, index) => {
    // 着色器函数（模拟 GLSL）
    const angle = time * 0.5;
    const wave = Math.sin(particle.x * 0.01 + time * 2) * 0.3;
    
    // 旋转矩阵
    const cosA = Math.cos(angle);
    const sinA = Math.sin(angle);
    
    const x = particle.x * cosA - particle.z * sinA;
    const z = particle.x * sinA + particle.z * cosA;
    const y = particle.y + wave * 50;
    
    return {
      ...particle,
      x,
      y,
      z,
    };
  });

  // 3D 投影到 2D
  const projectedParticles = updatedParticles.map((particle) => {
    const perspective = 800;
    const scale = perspective / (perspective + particle.z);
    
    return {
      x: width / 2 + particle.x * scale,
      y: height / 2 + particle.y * scale,
      scale,
      color: particle.color,
      z: particle.z,
    };
  });

  // 按 Z 轴排序（远到近）
  const sortedParticles = [...projectedParticles].sort((a, b) => a.z - b.z);

  // 中心文字动画
  const titleOpacity = spring({ frame, fps, config: { damping: 200 } });
  const titleY = interpolate(frame, [30, 60], [50, 0], {
    extrapolateRight: 'clamp',
    extrapolateLeft: 'clamp',
  });

  return (
    <AbsoluteFill style={{ background: 'radial-gradient(circle at center, #1a1a2e 0%, #0f0f1e 100%)' }}>
      {/* WebGL 粒子 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {sortedParticles.map((particle, index) => {
          const size = 5 * particle.scale;
          const opacity = 0.3 + particle.scale * 0.7;
          
          return (
            <g key={index}>
              {/* 粒子发光效果 */}
              <circle
                cx={particle.x}
                cy={particle.y}
                r={size * 2}
                fill={particle.color}
                opacity={opacity * 0.3}
                filter="url(#webgl-glow)"
              />
              
              {/* 粒子核心 */}
              <circle
                cx={particle.x}
                cy={particle.y}
                r={size}
                fill={particle.color}
                opacity={opacity}
              />
            </g>
          );
        })}
        
        {/* 发光滤镜 */}
        <defs>
          <filter id="webgl-glow">
            <feGaussianBlur stdDeviation="3" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, calc(-50% + ${titleY}px))`,
          opacity: titleOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            background: 'linear-gradient(90deg, #00ffff, #ff0080, #ffff00)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 30px rgba(0, 255, 255, 0.3)',
          }}
        >
          WebGL 着色器
        </div>
        <div
          style={{
            fontSize: 30,
            color: '#ffffff',
            marginTop: 10,
            opacity: 0.8,
          }}
        >
          GPU Accelerated Particles
        </div>
      </div>
    </AbsoluteFill>
  );
};

// ===== #278 AI 生成动画模式 =====

interface AIParticle {
  x: number;
  y: number;
  targetX: number;
  targetY: number;
  phase: number;
  amplitude: number;
  frequency: number;
}

export const AIGeneratedAnimationPattern: React.FC = () => {
  const frame = useCurrentFrame();
  const { width, height, fps } = useVideoConfig();

  // AI 生成的动画参数
  const aiParams = useMemo(() => ({
    pattern: 'spiral', // AI 选择的模式
    complexity: 5, // AI 生成的复杂度
    energy: 0.8, // AI 生成的能量级别
    coherence: 0.6, // AI 生成的连贯性
  }), []);

  // 生成 AI 控制的粒子（150 个）
  const particleCount = 150;
  const particles = useMemo(() => {
    const result: AIParticle[] = [];
    for (let i = 0; i < particleCount; i++) {
      result.push({
        x: width / 2,
        y: height / 2,
        targetX: 0,
        targetY: 0,
        phase: (i / particleCount) * Math.PI * 2,
        amplitude: 100 + Math.random() * 200,
        frequency: 1 + Math.random() * 2,
      });
    }
    return result;
  }, [width, height]);

  // AI 驱动的动画更新
  const time = frame / fps;
  const updatedParticles = particles.map((particle, index) => {
    // AI 生成的运动模式
    const spiralAngle = particle.phase + time * particle.frequency * aiParams.energy;
    const spiralRadius = particle.amplitude * (1 + Math.sin(time * 0.5) * aiParams.coherence);
    
    // 添加 AI 生成的随机扰动
    const noise = Math.sin(index * 0.1 + time * 3) * 20 * aiParams.complexity;
    
    const x = width / 2 + Math.cos(spiralAngle) * spiralRadius + noise;
    const y = height / 2 + Math.sin(spiralAngle) * spiralRadius + noise;
    
    return {
      ...particle,
      x,
      y,
    };
  });

  // 中心文字动画
  const titleOpacity = spring({ frame, fps, config: { damping: 200 } });
  const titleScale = spring({ frame, fps, config: { damping: 12 } });

  return (
    <AbsoluteFill style={{ background: 'linear-gradient(45deg, #0f0c29 0%, #302b63 50%, #24243e 100%)' }}>
      {/* AI 生成的粒子 */}
      <svg width={width} height={height} style={{ position: 'absolute' }}>
        {updatedParticles.map((particle, index) => {
          // AI 生成的颜色
          const hue = (index / particleCount) * 360 + time * 50;
          const saturation = 70 + aiParams.energy * 30;
          const lightness = 50 + aiParams.coherence * 20;
          
          // AI 控制的大小
          const size = 3 + Math.sin(index + time * 2) * 2;
          
          return (
            <g key={index}>
              {/* 粒子轨迹 */}
              <circle
                cx={particle.x}
                cy={particle.y}
                r={size * 3}
                fill={`hsl(${hue}, ${saturation}%, ${lightness}%)`}
                opacity={0.2}
                filter="url(#ai-glow)"
              />
              
              {/* 粒子核心 */}
              <circle
                cx={particle.x}
                cy={particle.y}
                r={size}
                fill={`hsl(${hue}, ${saturation}%, ${lightness + 20}%)`}
                opacity={0.8}
              />
            </g>
          );
        })}
        
        {/* 粒子连线（AI 生成的网络） */}
        {updatedParticles.slice(0, 50).map((particle, i) => {
          const nextParticle = updatedParticles[(i + 1) % 50];
          const distance = Math.sqrt(
            Math.pow(particle.x - nextParticle.x, 2) +
            Math.pow(particle.y - nextParticle.y, 2)
          );
          
          if (distance < 150) {
            return (
              <line
                key={`line-${i}`}
                x1={particle.x}
                y1={particle.y}
                x2={nextParticle.x}
                y2={nextParticle.y}
                stroke={`hsl(${(i / 50) * 360}, 70%, 60%)`}
                strokeWidth={1}
                opacity={0.3 * (1 - distance / 150)}
              />
            );
          }
          return null;
        })}
        
        {/* 发光滤镜 */}
        <defs>
          <filter id="ai-glow">
            <feGaussianBlur stdDeviation="4" result="blur" />
            <feMerge>
              <feMergeNode in="blur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>
      </svg>
      
      {/* 中心文字 */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: `translate(-50%, -50%) scale(${titleScale})`,
          opacity: titleOpacity,
          textAlign: 'center',
          zIndex: 10,
        }}
      >
        <div
          style={{
            fontSize: 80,
            fontWeight: 'bold',
            background: 'linear-gradient(135deg, #00ffff, #ff0080, #ffff00, #00ff00)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 40px rgba(0, 255, 255, 0.4)',
          }}
        >
          AI 生成动画
        </div>
        <div
          style={{
            fontSize: 30,
            color: '#ffffff',
            marginTop: 10,
            opacity: 0.8,
          }}
        >
          AI Generated Animation Pattern
        </div>
      </div>
      
      {/* AI 参数显示 */}
      <div
        style={{
          position: 'absolute',
          bottom: 40,
          left: 40,
          padding: 15,
          background: 'rgba(0, 0, 0, 0.6)',
          borderRadius: 10,
          backdropFilter: 'blur(10px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
        }}
      >
        <div style={{ fontSize: 16, color: '#00ffff', marginBottom: 5 }}>
          AI Parameters
        </div>
        <div style={{ fontSize: 14, color: '#ffffff', opacity: 0.8 }}>
          Pattern: {aiParams.pattern}
        </div>
        <div style={{ fontSize: 14, color: '#ffffff', opacity: 0.8 }}>
          Complexity: {aiParams.complexity}
        </div>
        <div style={{ fontSize: 14, color: '#ffffff', opacity: 0.8 }}>
          Energy: {(aiParams.energy * 100).toFixed(0)}%
        </div>
        <div style={{ fontSize: 14, color: '#ffffff', opacity: 0.8 }}>
          Coherence: {(aiParams.coherence * 100).toFixed(0)}%
        </div>
      </div>
    </AbsoluteFill>
  );
};
